/**
 * usePolledFetch — generic "fetch now, then keep fresh" hook for dashboard
 * panels backed by the live API.
 *
 * Contract:
 *   const { data, error, note, loading, lastUpdated, refresh } =
 *     usePolledFetch(fetcher, { intervalMs, enabled });
 *
 * - `fetcher` is any async function (normally a thin arrow over engine/api.js).
 * - `loading` is true only before the FIRST result; poll ticks refresh
 *   silently so panels never flicker back to a spinner (motion rule).
 * - On error the previous `data` is kept (stale beats blank mid-session) and
 *   `error` carries the message.
 * - `note` surfaces the backend's degraded-mode hint when the payload carries
 *   one ({ note: "..." }), so consumers can banner it without re-parsing.
 * - In-flight results are dropped after unmount; the interval is cleared.
 * - `deps` lists values that should trigger an immediate refetch when they
 *   change (e.g. a status filter); without them the ref'd fetcher would only
 *   pick up new closures on the next poll tick.
 * - `stopWhen(data)` is an optional predicate evaluated after each successful
 *   fetch. Once it returns true, interval ticks are suppressed (the data is a
 *   settled terminal state — e.g. a completed job — so there is nothing left to
 *   poll for). The data is kept, a manual refresh() still works, and the poll
 *   re-arms when `deps` change (a different target may not be terminal yet).
 */

import { useState, useEffect, useCallback, useRef } from "react";

export default function usePolledFetch(
  fetcher,
  { intervalMs = 30000, enabled = true, deps = [], stopWhen = null } = {},
) {
  const [state, setState] = useState({
    data: null,
    error: "",
    note: "",
    loading: true,
    lastUpdated: null,
  });

  // Latest fetcher without retriggering the effect when callers pass inline arrows.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const aliveRef = useRef(true);
  // Latest stopWhen predicate, so inline arrows don't churn the effect either.
  const stopWhenRef = useRef(stopWhen);
  stopWhenRef.current = stopWhen;
  // Set true once stopWhen is satisfied; suppresses the next interval ticks.
  const stoppedRef = useRef(false);
  // Monotonic request sequence for latest-request-wins. Both interval ticks and
  // manual refresh() calls share this; only the MOST-RECENTLY-ISSUED request may
  // write state, so an older in-flight response that resolves late (e.g. a 60s
  // poll started just before a manual refresh) can no longer overwrite newer
  // data with a stale value. Critical on money surfaces: without this a poll
  // that predates a redeem could clobber the post-redeem balance back to the old
  // one. (aliveRef only distinguishes mounted vs unmounted, not current vs stale.)
  const seqRef = useRef(0);

  const refresh = useCallback(async () => {
    const seq = (seqRef.current += 1);
    const isLatest = () => aliveRef.current && seq === seqRef.current;
    try {
      const data = await fetcherRef.current();
      if (!aliveRef.current) return undefined;
      if (isLatest()) {
        if (typeof stopWhenRef.current === "function" && stopWhenRef.current(data)) {
          stoppedRef.current = true;
        }
        setState({
          data,
          error: "",
          note: (data && typeof data === "object" && data.note) || "",
          loading: false,
          lastUpdated: new Date(),
        });
      }
      // Resolve to the fresh payload so callers awaiting THIS refresh() can act on
      // it immediately (state lands on the next render; e.g. JobOutputsPanel
      // re-presigns then previews with the fresh URL in one motion) — even if a
      // newer request has since superseded it for state-write purposes.
      return data;
    } catch (err) {
      if (!isLatest()) return undefined;
      setState((s) => ({ ...s, error: err.message || "Request failed", loading: false }));
      return undefined;
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    aliveRef.current = true;
    // Re-arm on (re)mount / deps change — a fresh target may not be terminal.
    stoppedRef.current = false;
    refresh();
    const id = setInterval(() => {
      // A settled terminal result suppresses ticks (manual refresh() still runs).
      if (stoppedRef.current) return;
      refresh();
    }, intervalMs);
    return () => {
      aliveRef.current = false;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, intervalMs, refresh, ...deps]);

  return { ...state, refresh };
}
